import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import { Loading } from 'src/components/Loading';
import { SCRoutes } from 'src/config';
import { EditUserForm } from './EditUserForm';
import { useDashboardGetUser, useSubmitUserUpdate } from './hooks';
import { UpdateUserInputs } from './users.types';

type Props = {
  testID?: string;
};

export const DashboardUsersEdit: React.FC<Props> = ({ testID = 'DashboardUsersEdit' }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const [isDirty, setIsDirty] = React.useState(false);

  const { id: userId } = useParams<{ id: string }>();

  const { user, fetchUser, error: fetchUserError, isLoading } = useDashboardGetUser();

  const { error: errorUpdatingUser, patchUser, userUpdated } = useSubmitUserUpdate();

  useEffect(() => {
    fetchUser(userId);
  }, [fetchUser, userId]);

  const onSubmit = useCallback(
    (data: UpdateUserInputs) => {
      if (data.role === 'SUPPERADMIN') {
        const result = window.confirm(t('dashboard-user-create.confirm-supperadmin'));

        if (!result) {
          return;
        }
      }

      patchUser(userId, data);
    },
    [patchUser, userId, t],
  );

  useEffect(() => {
    if (userUpdated) {
      setIsDirty(false);
      push(SCRoutes.dashboard.users.home);
    }
  }, [userUpdated, push, t, userId]);

  const onTouched = useCallback(
    (isDirty: boolean) => {
      if (userUpdated) {
        setIsDirty(false);
      }
      setIsDirty(isDirty);
    },
    [setIsDirty, userUpdated],
  );

  if (isLoading) {
    return <Loading />;
  }

  if (fetchUserError) {
    return <div className="content-center min-h-screen text-red-500">{fetchUserError.message}</div>;
  }

  if (!user) {
    return <div className="content-center min-h-screen text-red-500">{t('dashboard-user-edit.user-not-found')}</div>;
  }

  return (
    <>
      <Prompt when={isDirty} message={t('dashboard-user-create.confirm-exit-without-update')} />
      <div className="bg-white min-h-screen px-4">
        <div data-testid={testID} className="mx-4">
          <div
            className="text-xl text-gray-600"
            dangerouslySetInnerHTML={{
              __html: t('dashboard-user-update.page-title', { name: `${user.firstName} ${user.lastName}` }),
            }}
          />
        </div>

        <EditUserForm user={user} onTouched={onTouched} onSubmit={onSubmit} updateUserError={errorUpdatingUser} />
      </div>
    </>
  );
};
